import { TranslateUtils } from '../lib/utils/translate.utils';
import {
    isValidEmail,
    isValidUrl,
    isValidTelephone,
    isDateIncomplete,
    isFileSizeValid,
    isValidName,
    isValidCompanyName,
    isValidBirthday
} from '../lib/shared/validators/field-validators';

export interface ValidationError {
    fieldId: string;
    fieldName: string;
    message: string;
    element: HTMLElement;
    isCaptchaError?: boolean;
}

export interface ValidationResult {
    isValid: boolean;
    errors: ValidationError[];
}

export class FormValidator {

    public validate(form: HTMLFormElement): ValidationResult {
        const errors: ValidationError[] = [];

        // Get all form fields
        const fields = form.querySelectorAll<HTMLElement>('[data-element="field"]');

        fields.forEach(field => {
            const fieldErrors = this.validateField(field);
            errors.push(...fieldErrors);
        });

        const captchaErrors = this.validateCaptcha(form);
        errors.push(...captchaErrors);

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    public validateCaptcha(form: HTMLFormElement): ValidationError[] {
        const errors: ValidationError[] = [];

        const truCaptchaWidget = form.querySelector<HTMLElement>('[data-widget="captcha"][data-captcha-type="truCaptcha"]');
        if (truCaptchaWidget) {
            const input = truCaptchaWidget.querySelector<HTMLInputElement>('.tru-captcha-control');
            if (input && input.required && !input.value.trim()) {
                errors.push({
                    fieldId: 'captcha',
                    fieldName: TranslateUtils.FORM_VALIDATION.CAPTCHA,
                    message: `${TranslateUtils.FORM_VALIDATION.MISSING} ${TranslateUtils.FORM_VALIDATION.CAPTCHA}`,
                    element: input,
                    isCaptchaError: true
                });
            }
        }

        const recaptchaV2Widget = form.querySelector<HTMLElement>('[data-widget="captcha"][data-captcha-type="googleReCaptcha"]');
        if (recaptchaV2Widget) {
            const isInvisible = recaptchaV2Widget.getAttribute('data-recaptcha-invisible') === 'true';
            if (!isInvisible) {
                const token = recaptchaV2Widget.querySelector<HTMLTextAreaElement>('textarea[name="g-recaptcha-response"]')?.value;
                if (!token) {
                    const wrapper = recaptchaV2Widget.querySelector<HTMLElement>('.recaptcha-wrapper') || recaptchaV2Widget;
                    errors.push({
                        fieldId: 'recaptcha',
                        fieldName: TranslateUtils.FORM_VALIDATION.CAPTCHA,
                        message: `${TranslateUtils.FORM_VALIDATION.MISSING} ${TranslateUtils.FORM_VALIDATION.CAPTCHA}`,
                        element: wrapper,
                        isCaptchaError: true
                    });
                }
            }
        }

        return errors;
    }

    public validateTruCaptcha(form: HTMLFormElement): ValidationError[] {
        return this.validateCaptcha(form).filter(e => e.fieldId === 'captcha');
    }

    public validateField(field: HTMLElement): ValidationError[] {
        const errors: ValidationError[] = [];
        const fieldType = field.dataset['field'];
        const fieldId = field.id;

        const input = this.getFieldInput(field);
        if (!input) return errors;

        const label = input.dataset['label'] || this.getFieldLabel(field) || 'This field';
        const value = this.getFieldValue(field, fieldType);

        const hasCustomRequiredField =
            fieldType === 'checkbox' ||
            fieldType === 'gdprPolicyConsent' ||
            fieldType === 'radio' ||
            fieldType === 'signature' ||
            fieldType === 'file';
        const isRequired = hasCustomRequiredField
            ? field.querySelector('.checkbox, .radio, .field__radio, .gdpr-policy-consent, .signature-placeholder, .file-select-label')?.classList.contains('required') ?? false
            : input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement
                ? input.required || field.classList.contains('required')
                : field.classList.contains('required');

        if (isRequired && !value) {
            const message = fieldType === 'subscribe'
                ? `${TranslateUtils.FORM_VALIDATION.SUBSCRIBE.REQUIRED}`
                : `${TranslateUtils.FORM_VALIDATION.MISSING} ${label}`;
            errors.push({
                fieldId,
                fieldName: label,
                message: message,
                element: input
            });
            return errors;
        }

        if (!value) {
            return errors;
        }

        switch (fieldType) {
            case 'email':
                if (!isValidEmail(value)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.EMAIL.INCORRECT,
                        element: input
                    });
                }
                break;

            case 'firstname':
                if (!isValidName(value)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.FIRSTNAME.INCORRECT,
                        element: input
                    });
                }
                break;

            case 'lastname':
                if (!isValidName(value)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.LASTNAME.INCORRECT,
                        element: input
                    });
                }
                break;

            case 'companyname':
                if (!isValidCompanyName(value)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.COMPANYNAME.INCORRECT,
                        element: input
                    });
                }
                break;

            case 'signature':
                break;

            case 'subscribe':
                break;

            case 'birthday':
                if (isDateIncomplete(value)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.DATE.INCOMPLETE_DATE,
                        element: input
                    });
                }

                const format = input?.dataset['format'] ?? 'MM/dd/yyyy';
                if (!isValidBirthday(value, format)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.BIRTHDAY.INCORRECT,
                        element: input
                    });
                }
                break;

            case 'url':
                if (!isValidUrl(value)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.URL.INCORRECT,
                        element: input
                    });
                }
                break;

            case 'tel':
                if (!isValidTelephone(value)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.TEL.INVALID_CHARACTERS,
                        element: input
                    });
                }
                break;

            case 'number':
                if (input instanceof HTMLInputElement) {
                    const numValue = parseFloat(value);
                    if (isNaN(numValue)) {
                        errors.push({
                            fieldId,
                            fieldName: label,
                            message: TranslateUtils.FORM_VALIDATION.NUMBER.INVALID_NUMBER,
                            element: input
                        });
                    } else {
                        if (input.min && numValue < parseFloat(input.min)) {
                            errors.push({
                                fieldId,
                                fieldName: label,
                                message: `${TranslateUtils.FORM_VALIDATION.NUMBER.OVER_MIN_VALUE} ${input.min}`,
                                element: input
                            });
                        }
                        if (input.max && numValue > parseFloat(input.max)) {
                            errors.push({
                                fieldId,
                                fieldName: label,
                                message: `${TranslateUtils.FORM_VALIDATION.NUMBER.OVER_MAX_VALUE} ${input.max}`,
                                element: input
                            });
                        }
                    }
                }
                break;

            case 'checkbox': {
                const fieldset = field.querySelector<HTMLElement>('.checkbox');
                const checkedInputs = fieldset?.querySelectorAll<HTMLInputElement>('.input:checked');
                const notCheckedInputs = fieldset?.querySelectorAll<HTMLInputElement>('.input:not(:checked)');
                const checkedCount = checkedInputs?.length ?? 0;

                const optionsContainer = field.querySelector<HTMLElement>('.options');
                const errorTarget = optionsContainer || input;

                if (checkedCount) {
                    const minCount = fieldset?.getAttribute('min-count');
                    if (minCount && parseInt(minCount) > checkedCount) {
                        notCheckedInputs?.forEach(input => input.classList.add('min-error'));

                        errors.push({
                            fieldId,
                            fieldName: label,
                            message: `${TranslateUtils.FORM_VALIDATION.CHECKBOX.OVER_MIN_VALUE} ${minCount}`,
                            element: errorTarget
                        });
                    }

                    const maxCount = fieldset?.getAttribute('max-count');
                    if (maxCount && parseInt(maxCount) < checkedCount) {
                        checkedInputs?.forEach(input => input.classList.add('max-error'));

                        errors.push({
                            fieldId,
                            fieldName: label,
                            message: `${TranslateUtils.FORM_VALIDATION.CHECKBOX.OVER_MAX_VALUE} ${maxCount}`,
                            element: errorTarget
                        });
                    }
                } else if (isRequired) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: `${TranslateUtils.FORM_VALIDATION.MISSING} ${label}`,
                        element: errorTarget
                    });
                }
                break;
            }

            case 'radio': {
                const radios = field.querySelectorAll<HTMLInputElement>('input[type="radio"]');
                const anySelected = Array.from(radios).some(r => r.checked);
                const radioOptionsContainer = field.querySelector<HTMLElement>('.options');
                const radioErrorTarget = radioOptionsContainer || radios[0] || input;

                if (isRequired && !anySelected) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: `${TranslateUtils.FORM_VALIDATION.MISSING} ${label}`,
                        element: radioErrorTarget
                    });
                }
                break;
            }

            case 'file':
                if (input instanceof HTMLInputElement) {
                    const fileWrapper = field.querySelector<HTMLElement>('.file-select-label') || input;

                    if (isRequired && (!input.files || input.files.length === 0)) {
                        errors.push({
                            fieldId,
                            fieldName: label,
                            message: `${TranslateUtils.FORM_VALIDATION.MISSING} ${label}`,
                            element: fileWrapper
                        });
                    } else if (input.files && input.files.length > 0) {
                        const file = input.files[0];
                        if (!isFileSizeValid(file)) {
                            errors.push({
                                fieldId,
                                fieldName: label,
                                message: TranslateUtils.FORM_VALIDATION.FILE_TOO_LARGE,
                                element: fileWrapper
                            });
                        }
                    }
                }
                break;

            case 'date':
                if (isDateIncomplete(value)) {
                    errors.push({
                        fieldId,
                        fieldName: label,
                        message: TranslateUtils.FORM_VALIDATION.DATE.INCOMPLETE_DATE,
                        element: input
                    });
                }
                break;

            case 'password': {
                const passwordInput = field.querySelector<HTMLInputElement>('.field:not(.password-confirmation) .input');
                const confirmInput = field.querySelector<HTMLInputElement>('.password-confirmation .input');
                const strengthRulesContainer = field.querySelector('.password-strength-rules');

                // Validate password strength if enabled
                if (passwordInput && strengthRulesContainer && value) {
                    const strengthError = this.validatePasswordStrength(value);
                    if (strengthError) {
                        errors.push({
                            fieldId,
                            fieldName: label,
                            message: strengthError,
                            element: passwordInput
                        });
                    }
                }

                // Validate confirmation match
                if (confirmInput && passwordInput) {
                    const confirmValue = confirmInput.value.trim();
                    const confirmLabel = confirmInput.dataset['label'] || 'Confirm Password';

                    if (confirmInput.required && !confirmValue) {
                        errors.push({
                            fieldId,
                            fieldName: confirmLabel,
                            message: `${TranslateUtils.FORM_VALIDATION.MISSING} ${confirmLabel}`,
                            element: confirmInput
                        });
                    } else if (confirmValue && confirmValue !== value) {
                        errors.push({
                            fieldId,
                            fieldName: confirmLabel,
                            message: TranslateUtils.FORM_VALIDATION.PASSWORD?.MISMATCH ?? 'Passwords do not match',
                            element: confirmInput
                        });
                    }
                }
                break;
            }
        }

        return errors;
    }

    private validatePasswordStrength(password: string): string | null {
        if (password.length < 10) {
            return TranslateUtils.PASSWORD_FIELD.STRENGTH.MIN_LENGTH;
        }

        if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) {
            return TranslateUtils.PASSWORD_FIELD.STRENGTH.MIXED_CASE;
        }

        if (!/\d/.test(password)) {
            return TranslateUtils.PASSWORD_FIELD.STRENGTH.HAS_NUMBER;
        }

        return null;
    }

    private getFieldValue(field: HTMLElement, fieldType?: string): string {
        if (fieldType === 'checkbox' || fieldType === 'gdprPolicyConsent' || fieldType === 'subscribe') {
            const checkboxes = field.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked');
            return checkboxes.length > 0 ? 'checked' : '';
        }

        if (fieldType === 'radio') {
            const selected = field.querySelector<HTMLInputElement>('input[type="radio"]:checked');
            return selected?.value || '';
        }

        const input = field.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select'
        );

        return input?.value?.trim() || '';
    }

    private getFieldLabel(field: HTMLElement): string {
        const label = field.querySelector('.label');
        return label?.textContent?.replace('*', '').trim() || '';
    }

    private getFieldInput(field: HTMLElement): HTMLElement | null {
        const fieldType = field.dataset['field'];
        let inputSelector: string;
        switch (fieldType) {
            case 'checkbox':
                inputSelector = '.options';
                break;

            case 'gdprPolicyConsent':
                inputSelector = 'input[type="checkbox"]';
                break;

            case 'radio':
                inputSelector = '.options';
                break;

            case 'signature':
                inputSelector = '.signature-placeholder';
                break;

            case 'file':
                inputSelector = '.file-select-label';
                break;

            default:
                inputSelector = 'input:not([type="hidden"]), textarea, select';
        }

        return field.querySelector<HTMLElement>(inputSelector);
    }


    private isDateIncomplete(value: string): boolean {
        const hasDigits = /\d/.test(value);
        const hasPlaceholders = /[DMY]/.test(value);
        return hasDigits && hasPlaceholders;
    }

    public showErrors(errors: ValidationError[]): void {
        // Clear previous errors first
        this.clearErrors(errors.map(e => e.element));

        errors.forEach(error => {
            this.showFieldError(error);
        });
    }

    public showFieldError(error: ValidationError): void {
        const { element, message } = error;

        // Add error class
        element.classList.add('error');
        element.setAttribute('aria-invalid', 'true');

        // Find or create error container (check both field and widget)
        const container = element.closest('[data-element="field"]') || element.closest('[data-element="widget"]');
        let errorContainer = container?.querySelector('.fieldJsonData-errors');

        if (!errorContainer) {
            errorContainer = document.createElement('div');
            errorContainer.classList.add('fieldJsonData-errors');

            const optionsContainer = container?.querySelector('.options') || container?.querySelector('.checkbox') || container?.querySelector('.gdpr-policy-consent');
            if (optionsContainer) {
                optionsContainer.insertAdjacentElement('afterend', errorContainer);
            } else {
                element.insertAdjacentElement('afterend', errorContainer);
            }
        }

        // Add error message
        const errorSpan = document.createElement('span');
        errorSpan.classList.add('error-message');
        errorSpan.textContent = message;
        errorContainer.appendChild(errorSpan);
    }

    public clearErrors(elements?: HTMLElement[]): void {
        const targets = elements || [];

        targets.forEach(element => {
            element.classList.remove('error');
            element.setAttribute('aria-invalid', 'false');

            const container = element.closest('[data-element="field"]') || element.closest('[data-element="widget"]');
            const errorContainer = container?.querySelector('.fieldJsonData-errors');
            if (errorContainer) {
                errorContainer.innerHTML = '';
            }
        });
    }

    public clearAllErrors(form: HTMLFormElement): void {
        const errorElements = form.querySelectorAll<HTMLElement>('.error');
        errorElements.forEach(el => {
            el.classList.remove('error');
            el.setAttribute('aria-invalid', 'false');
        });

        const errorContainers = form.querySelectorAll('.fieldJsonData-errors');
        errorContainers.forEach(container => {
            container.innerHTML = '';
        });
    }

    public scrollToFirstError(errors: ValidationError[]): void {
        if (errors.length === 0) return;

        const firstError = errors[0];
        const target = firstError.element.closest('[data-element="field"]')
            || firstError.element.closest('[data-element="widget"]')
            || firstError.element;

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}
